import { UserAccountSettings } from "@WahooFitness/cloud-client-ts";
import { UserAccountSettingsUpdateType } from "@WahooFitness/cloud-client-types";
import { useCloudContext, useConfigContext, useOfflineSWR } from "@WahooFitness/wahoo-offline-mfe";
import { useCallback, useMemo } from "react";

const useUserAccountSettings = () => {
  const { wahooToken } = useConfigContext();
  const { getCloudClient } = useCloudContext();
  const client = useMemo(() => getCloudClient(UserAccountSettings), [getCloudClient]);

  const {
    data: userAccountSettingsData,
    isLoading: userAccountSettingsIsLoading,
    error: userAccountSettingsError,
    mutate: mutateUserAccountSettings,
  } = useOfflineSWR(
    () => ["userAccountSettings", wahooToken],
    ([_key, token]) => client.get(token)
  );

  const updateUserAccountSettings = useCallback(
    async (updates: UserAccountSettingsUpdateType) => {
      if (!userAccountSettingsData) {
        return;
      }
      // rollbackOnError keeps a rejected update's optimistic value out of the cache;
      // the resolved value re-seeds it with the server's row.
      await mutateUserAccountSettings(client.update(wahooToken, updates), {
        optimisticData: { ...userAccountSettingsData, ...updates },
        rollbackOnError: true,
        populateCache: true,
        revalidate: false,
      });
    },
    [client, mutateUserAccountSettings, userAccountSettingsData, wahooToken]
  );

  // Fetches the row again, for a setting another device may have changed (TAJ-1641).
  const reloadUserAccountSettings = useCallback(
    () => mutateUserAccountSettings(),
    [mutateUserAccountSettings]
  );

  return {
    userAccountSettingsData,
    userAccountSettingsIsLoading,
    userAccountSettingsError,
    updateUserAccountSettings,
    reloadUserAccountSettings,
  };
};

export default useUserAccountSettings;
